/**
 * useIsKeyboardOpen — reliable keyboard detection for mobile
 *
 * Primary signal: focus tracking on input/textarea elements.
 * When a text input or textarea has focus on a mobile device,
 * the keyboard is open.
 *
 * Secondary signal: visualViewport height delta and
 * --app-keyboard-height CSS variable for edge cases.
 */

import { useState, useEffect } from 'react';

const INPUT_SELECTORS = 'input, textarea, [contenteditable]';

export function useIsKeyboardOpen(): boolean {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Element | null;
      if (target?.matches?.(INPUT_SELECTORS)) {
        setIsOpen(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      // relatedTarget is the element receiving focus next.
      // If it's also an input, keyboard stays open (field-to-field).
      const next = e.relatedTarget as Element | null;
      if (next?.matches?.(INPUT_SELECTORS)) return;

      // Small delay: on iOS/Telegram, focusout fires briefly
      // before focusin when tapping between inputs
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (!document.activeElement?.matches(INPUT_SELECTORS)) {
          setIsOpen(false);
        }
      }, 100);
    };

    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      // The check must not outlive the page: it reads the document after unmount.
      clearTimeout(closeTimer);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  return isOpen;
}
